
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Hero } from "./Hero";
import { HowItWorks } from "./HowItWorks";
import { WhyItWorks } from "./WhyItWorks";
import { FixtureAutopilot } from "./FixtureAutopilot";
import { DemoPoll } from "@/components/home/DemoPoll";
import { isAuthenticated, saveAuthToken } from "@/utils/storage";
import { supabase } from "@/integrations/supabase/client";
import { CreateGameButton } from "./CreateGameButton";
import { GamesList } from "./GamesList";
import { SEO } from "@/components/seo/seo";
import { SisterFeatureLinks } from "@/components/marketing/sister-feature-links";
import { marketing, useMarketingLocale } from "@/i18n/marketing";
import { alternatesFor, canonicalFor, htmlLangFor } from "@/components/seo/marketing-seo";
import { LocaleRedirect } from "@/components/marketing/locale-redirect";

const homeJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Vote.GameOn",
    url: "https://vote.gameon.futbol",
    logo: "https://vote.gameon.futbol/logo.png",
    sameAs: ["https://team-vote.lovable.app"],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Vote.GameOn.Futbol",
    url: "https://vote.gameon.futbol",
    description:
      "Frictionless platform for grassroots football: WhatsApp-native game RSVP, player-of-the-match voting, and pro-grade RPE exertion tracking.",
  },
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Vote.GameOn",
    applicationCategory: "SportsApplication",
    operatingSystem: "Web",
    description:
      "Player-of-the-match voting, game RSVP, and exertion tracking for grassroots football coaches. No app required for players or parents.",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  },
];

const Index = () => {
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const navigate = useNavigate();
  const locale = useMarketingLocale();
  const seo = marketing.home[locale].seo;
  const canonical = canonicalFor("/", locale);
  const alternates = alternatesFor("/");
  
  useEffect(() => {
    const checkSession = async () => {
      // Don't proceed if already redirecting
      if (redirecting) return;
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          console.log("Existing session found in Index page:", session);
          
          if (session.access_token && session.refresh_token) {
            saveAuthToken(btoa(JSON.stringify(session.user)));
            setSession(session);
            
            // Redirect to the teams page
            setRedirecting(true);
            navigate("/teams", { replace: true });
            return;
          }
        }
        
        // If we reach here, no valid session found
        setIsLoading(false);
      } catch (error) {
        console.error("Error checking session:", error);
        setIsLoading(false);
      }
    };
    
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed in Index page:", event, session);
        
        if (session) {
          saveAuthToken(btoa(JSON.stringify(session.user)));
          setSession(session);
          
          if (event === 'SIGNED_IN' && session.access_token && session.refresh_token) {
            // Redirect to the teams page on sign in
            setRedirecting(true);
            navigate("/teams", { replace: true });
            return;
          }
        } else if (event === 'SIGNED_OUT') {
          setSession(null);
          setIsLoading(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, redirecting]);

  // Get authentication status from local storage, don't rely only on session
  const isCoach = isAuthenticated();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="max-container flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <LocaleRedirect />
      <SEO
        title={seo.title}
        description={seo.description}
        canonicalPath={canonical}
        locale={htmlLangFor(locale)}
        alternates={alternates}
        jsonLd={homeJsonLd}
      />
      <div className="max-container"> 
        <Hero />
      </div>
      
      <HowItWorks />

      {!isCoach && !session && <FixtureAutopilot />}

      {!isCoach && !session && (
        <WhyItWorks />
      )}
      
      {!isCoach && !session && (
        <div className="max-container py-8">
          <DemoPoll />
        </div>
      )}
      
      {isCoach && (
        <div className="max-container">
          <CreateGameButton />
          <GamesList />
        </div>
      )}

      {!isCoach && !session && <SisterFeatureLinks current="home" />}
    </>
  );
};

export default Index;
